/**
 * Oregon In-Home Care Agency State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: OAR Chapter 333-536 (In-Home Care Agencies)
 *   Last major revision: October 23, 2023
 * - Statutory Authority: ORS 443.305-443.355
 * - Criminal Records Check: ORS 443.004, OAR 333-536-0093
 * - Background Check Unit (BCU): OAR 407-007-0200 to 407-007-0370 (Medicaid clients)
 * 
 * IMPORTANT DISTINCTION:
 * - OAR 333-536: In-Home Care Agencies (personal care, non-medical)
 * - OAR 333-027: Home Health Agencies (skilled nursing, therapy - DIFFERENT LICENSE)
 * 
 * REGULATORY BODY:
 * Oregon Health Authority (OHA) - Public Health Division
 * Health Care Regulation and Quality Improvement (HCRQI)
 * Phone: (971) 673-0540
 * Website: https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - License term: 12 months (annual) per OAR 333-536-0025
 * - Renewal application: 30 days before expiration per 333-536-0025
 * - Survey frequency: Every 2 years per 333-536-0041(1)
 * - Four license classifications: Limited, Basic, Intermediate, Comprehensive per 333-536-0007
 * - Record retention: 7 years after last end-of-service per 333-536-0085(9)
 * - Initial visit: 7-30 days after start of care per 333-536-0066(1)(a)
 * - Monitoring visits: Quarterly per 333-536-0066(1)(b)
 * - Service plan: Within 7 days of service initiation per 333-536-0065(3)
 * - Criminal records check: Every 3 years per 333-536-0093(13)
 * - Administrator: HS diploma + 2 years experience per 333-536-0052(1)
 * - Caregiver orientation: 4 hours per 333-536-0070(5)
 * - Initial caregiver training: 8 hours per 333-536-0070(7)
 * - Medication training: 4 hours per 333-536-0070(8)
 * - Annual education: 6 hours (7 if medication services) per 333-536-0070(14)
 * 
 * Last Verified: December 2024 against current OAR 333-536
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const OREGON_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & CLASSIFICATION
  // ============================================
  {
    id: 'OR-A1',
    itemId: 'A1',
    stateCode: 'OR',
    title: 'In-Home Care Agency License Status',
    description: 'Oregon requires all in-home care agencies to be licensed by OHA per ORS 443.315',
    severity: 'high',
    regulationCode: '333-536-0010',
    regulationName: 'License',
    agencyName: 'Oregon Health Authority - Public Health Division',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active In-Home Care Agency license from OHA?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 333-536-0010(1): Agency must be licensed to provide in-home care services. Operating without license prohibited per 333-536-0105.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'License expires 12 months from issue date per 333-536-0025. Submit renewal at least 30 days before expiration.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your OHA In-Home Care Agency license is current per OAR 333-536.',
      atRisk: 'Your license expires within 60 days. Submit renewal at least 30 days before expiration per 333-536-0025.',
      nonCompliant: 'Operating without a valid license violates ORS 443.315 and OAR 333-536-0105. Civil penalties apply per 333-536-0125.',
      unknown: 'Verify your In-Home Care Agency license status with OHA at (971) 673-0540.',
    },
    stateNotes: [
      'License valid for 12 months per 333-536-0025',
      'Submit renewal 30 days before expiration',
      'License fee per ORS 443.315',
      'License not transferable per 333-536-0021(2)',
      'Must post license conspicuously per 333-536-0021(3)',
    ],
  },

  {
    id: 'OR-A2',
    itemId: 'A2',
    stateCode: 'OR',
    title: 'License Classification',
    description: 'Oregon has four license classifications based on services provided per OAR 333-536-0007',
    severity: 'high',
    regulationCode: '333-536-0007',
    regulationName: 'Classification',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'What is your agency\'s license classification?',
        inputType: 'select',
        options: [
          { value: 'limited', label: 'Limited - Personal care + medication reminding only' },
          { value: 'basic', label: 'Basic - + medication assistance' },
          { value: 'intermediate', label: 'Intermediate - + medication administration' },
          { value: 'comprehensive', label: 'Comprehensive - + nursing services' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
        helpText: 'Per 333-536-0007: Classification determines services you may provide.',
      },
      {
        id: 'q2',
        question: 'Do you provide ONLY services authorized by your classification?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - only services within classification scope' },
          { value: 'no', label: 'No - may provide services beyond classification' },
        ],
        helpText: 'Per 333-536-0007(2): Cannot provide services or use title other than licensed classification.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency services align with license classification.',
      atRisk: 'Verify your license classification with OHA.',
      nonCompliant: 'Providing services outside license classification violates 333-536-0007(2).',
      unknown: 'Review classification requirements.',
    },
    stateNotes: [
      'Limited: Personal care + medication reminding (no assistance/administration)',
      'Basic: + medication assistance (client must self-direct)',
      'Intermediate: + medication administration (requires RN)',
      'Comprehensive: + nursing services (requires RN)',
      'Cannot use title other than licensed classification per 333-536-0007(2)',
    ],
  },

  {
    id: 'OR-A3',
    itemId: 'A3',
    stateCode: 'OR',
    title: 'Administrator Qualifications',
    description: 'Oregon requires qualified administrators per OAR 333-536-0052',
    severity: 'high',
    regulationCode: '333-536-0052',
    regulationName: 'Administrator',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet Oregon qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'hs_diploma', label: 'High school diploma' },
          { value: 'experience', label: '2+ years professional or management experience in health-related field' },
          { value: 'onsite', label: 'On-site at agency multiple days per week per 333-536-0052(6)(a)' },
          { value: 'accessible', label: 'Accessible during all service hours per 333-536-0052(5)' },
        ],
        required: true,
        helpText: 'Per 333-536-0052(1): "Qualified" means HS diploma + 2 years experience.',
      },
      {
        id: 'q2',
        question: 'Is the administrator (or qualified designee) available during ALL hours services are provided?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 333-536-0052(5): Administrator or designee must be accessible and available during all service hours.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets OAR 333-536-0052 requirements.',
      atRisk: 'Administrator qualifications may be incomplete.',
      nonCompliant: 'Administrator does not meet 333-536-0052 requirements.',
      unknown: 'Verify administrator qualifications.',
    },
    stateNotes: [
      'Qualified = HS diploma + 2 years professional/management experience per 333-536-0052(1)',
      'Experience must be in health-related field or program',
      'Must be on-site multiple days per week per 333-536-0052(6)(a)',
      'Must be accessible during all service hours per 333-536-0052(5)',
      'Designee must also meet all administrator qualifications per 333-536-0052(3)',
    ],
  },

  {
    id: 'OR-A4',
    itemId: 'A4',
    stateCode: 'OR',
    title: 'Change Notification Requirements',
    description: 'Oregon requires notification of changes within 30 days per OAR 333-536-0010(5)',
    severity: 'medium',
    regulationCode: '333-536-0010(5)',
    regulationName: 'Notification',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Is all information on your license application current with OHA?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes - all information is current' },
          { value: 'pending', label: 'Changes pending - need to notify OHA' },
          { value: 'outdated', label: 'No - information is outdated' },
        ],
        required: true,
        helpText: 'Per 333-536-0010(5): Submit revised application within 30 days of any change.',
      },
      {
        id: 'q2',
        question: 'For the following changes, do you obtain OHA pre-approval BEFORE implementation?',
        inputType: 'checklist',
        options: [
          { value: 'administrator', label: 'Administrator change' },
          { value: 'classification', label: 'Agency classification change' },
          { value: 'branch', label: 'Branch or subunit addition/removal' },
        ],
        helpText: 'Per 333-536-0010(5)(a): These changes require OHA approval before implementation.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'current', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'outdated', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency information is current with OHA.',
      atRisk: 'Submit revised application within 30 days of changes per 333-536-0010(5).',
      nonCompliant: 'Failure to report changes violates 333-536-0010(5).',
      unknown: 'Verify agency information is current with OHA.',
    },
    stateNotes: [
      'Revised application within 30 days of any change per 333-536-0010(5)',
      'Administrator, classification, branch/subunit changes require pre-approval per 333-536-0010(5)(a)',
      'Change of ownership requires form and fee per 333-536-0021(2)',
    ],
  },

  {
    id: 'OR-A5',
    itemId: 'A5',
    stateCode: 'OR',
    title: 'Geographic Service Area Compliance',
    description: 'Oregon limits service area to 60 miles from agency location per OAR 333-536-0050(2)',
    severity: 'medium',
    regulationCode: '333-536-0050(2)',
    regulationName: 'Service Area',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide services only within 60 miles of your agency or subunit location?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - all services within 60 miles' },
          { value: 'travel', label: 'Sometimes caregivers travel with clients beyond 60 miles' },
          { value: 'no', label: 'No - regularly serve beyond 60 miles' },
        ],
        required: true,
        helpText: 'Per 333-536-0050(2)(b): Service area within 60 miles. Exceptions for travel with clients.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'travel', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Services provided within geographic service area.',
      atRisk: 'Travel with clients allowed with proper documentation per 333-536-0050(2)(b)(A).',
      nonCompliant: 'Providing services beyond 60 miles requires subunit license.',
      unknown: 'Review geographic service area requirements.',
    },
    stateNotes: [
      'Geographic service area: 60 miles from parent agency or subunit per 333-536-0005(12)',
      'Caregiver may travel with client if documented in service plan per 333-536-0050(2)(b)(A)',
      'Beyond 60 miles requires separate subunit license per 333-536-0050(4)',
      'Branch office must be within 60 miles of parent per 333-536-0050(3)',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING
  // ============================================
  {
    id: 'OR-B1',
    itemId: 'B1',
    stateCode: 'OR',
    title: 'Criminal Records Check Requirements',
    description: 'Oregon requires criminal records checks per OAR 333-536-0093',
    severity: 'high',
    regulationCode: '333-536-0093',
    regulationName: 'Background Check',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct criminal records checks for ALL subject individuals before they have direct client contact?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always before direct contact' },
          { value: 'preliminary', label: 'Sometimes use preliminary hire (supervised, no client contact)' },
          { value: 'no', label: 'No - some may start before check complete' },
        ],
        required: true,
        helpText: 'Per 333-536-0093(2): Check required before hiring or allowing volunteer status for anyone with direct client contact.',
      },
      {
        id: 'q2',
        question: 'Do your criminal records checks include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'name_trace', label: 'Name and address history trace - 333-536-0093(10)(a)' },
          { value: 'ssn_dob', label: 'SSN trace and DOB verification - 333-536-0093(10)(b)' },
          { value: 'local_7yr', label: 'Local criminal records (7 years) - 333-536-0093(10)(c)' },
          { value: 'nationwide', label: 'Nationwide criminal database search - 333-536-0093(10)(d)' },
          { value: 'sex_offender', label: 'Nationwide sex offender registry - 333-536-0093(10)(e)' },
          { value: 'leie', label: 'LEIE (List of Excluded Individuals/Entities) - 333-536-0093(11)' },
        ],
        helpText: 'Per 333-536-0093(10-11): All elements required.',
      },
      {
        id: 'q3',
        question: 'Do you renew criminal records checks every 3 years?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 333-536-0093(13): Check required every 3 years.',
      },
      {
        id: 'q4',
        question: 'Do employees begin working with clients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per OAR 333-536-0093(2): NO direct client contact is allowed until background check clearance is received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation - must be evaluated FIRST)
      { condition: 'equals', questionId: 'q4', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if starting work before check complete (from q1)
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required check elements
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if not renewing every 3 years
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT only if all conditions met AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['always', 'preliminary'] },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 6, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal records check procedures meet OAR 333-536-0093 requirements.',
      atRisk: 'Criminal records check procedures may be incomplete.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per OAR 333-536-0093(2), NO direct client contact is allowed until background check clearance is received. Immediately remove uncleared staff from client care.',
      unknown: 'Verify criminal records check procedures.',
    },
    stateNotes: [
      'Check required before hiring or direct contact per 333-536-0093(2)',
      'Vendor must be NAPBS-accredited or meet criteria in 333-536-0093(8)',
      'Check every 3 years per 333-536-0093(13)',
      'LEIE check required per 333-536-0093(11)',
      'Preliminary hire allowed with no client contact per 333-536-0093(4)',
      'Disqualifying crimes in ORS 443.004(3) - permanent bar',
    ],
  },

  {
    id: 'OR-B2',
    itemId: 'B2',
    stateCode: 'OR',
    title: 'BCU Background Check (Medicaid Clients)',
    description: 'Medicaid/ODHS clients require BCU background check per OAR 333-536-0093(9)',
    severity: 'high',
    regulationCode: '333-536-0093(9)',
    regulationName: 'BCU Check',
    agencyName: 'ODHS Background Check Unit',
    agencyAbbr: 'BCU',
    agencyPhone: '(503) 378-5470',
    agencyUrl: 'https://www.oregon.gov/dhs/BACKGROUND-CHECK',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide services to Medicaid/ODHS clients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For staff serving Medicaid/ODHS clients, do you submit background checks through the BCU?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all staff serving ODHS clients' },
          { value: 'some', label: 'Some staff - not consistent' },
          { value: 'no', label: 'No - using other vendor' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 333-536-0093(9): BCU required for staff working with ODHS clients.',
      },
      {
        id: 'q3',
        question: 'Do you maintain BCU fitness determinations and renew every 3 years for portability?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 333-536-0093(9)(d): BCU recheck every 3 years maintains portability.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'BCU background checks properly conducted for Medicaid clients.',
      atRisk: 'BCU background checks may be inconsistent for Medicaid clients.',
      nonCompliant: 'Staff serving ODHS/Medicaid clients require BCU background check.',
      unknown: 'Verify BCU requirements if serving Medicaid clients.',
    },
    stateNotes: [
      'BCU required for staff with ODHS clients per 333-536-0093(9)',
      'BCU makes final fitness determination per 333-536-0093(9)(c)',
      'BCU approval provides portability per 333-536-0093(9)(c)',
      'Recheck every 3 years maintains portability per 333-536-0093(9)(d)',
      'BCU check satisfies vendor requirement per 333-536-0093(9)(f)',
    ],
  },

  {
    id: 'OR-B3',
    itemId: 'B3',
    stateCode: 'OR',
    title: 'Fitness Determination and Weighing Test Policy',
    description: 'Oregon requires written policy for fitness determinations per OAR 333-536-0093(5-6)',
    severity: 'high',
    regulationCode: '333-536-0093(5)',
    regulationName: 'Fitness Policy',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy for criminal records check fitness determinations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - written policy in place' },
          { value: 'no', label: 'No written policy' },
        ],
        required: true,
        helpText: 'Per 333-536-0093(5): Written policy required including weighing test procedures.',
      },
      {
        id: 'q2',
        question: 'Does your policy include the required weighing test factors?',
        inputType: 'checklist',
        options: [
          { value: 'nature', label: 'Nature of crime and incident details - 333-536-0093(6)(a)(A)' },
          { value: 'relevancy', label: 'Relevancy to position - 333-536-0093(6)(a)(C)' },
          { value: 'time', label: 'Passage of time since offense - 333-536-0093(6)(a)(D)(i)' },
          { value: 'age', label: 'Age at time of offense - 333-536-0093(6)(a)(D)(ii)' },
          { value: 'treatment', label: 'Evidence of treatment/rehabilitation - 333-536-0093(6)(b)(E)' },
          { value: 'access', label: 'Relevancy to access (meds, info, vulnerable populations) - 333-536-0093(6)(c)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 333-536-0093(6): Weighing test must consider these factors.',
      },
      {
        id: 'q3',
        question: 'Do you document weighing test decisions in employee files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 333-536-0093(7): Document weighing test and place in employee file.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Fitness determination policy meets 333-536-0093 requirements.',
      atRisk: 'Fitness determination policy may be incomplete.',
      nonCompliant: 'Written fitness determination policy required.',
      unknown: 'Verify fitness determination procedures.',
    },
    stateNotes: [
      'Written policy required per 333-536-0093(5)',
      'ORS 443.004(3) crimes = permanent bar per 333-536-0093(5)(a)',
      'Weighing test for other crimes per 333-536-0093(5)(c)',
      'Document weighing test in employee file per 333-536-0093(7)',
      'Consider access to meds, personal info, vulnerable populations per 333-536-0093(6)(c)',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL RECORDS
  // ============================================
  {
    id: 'OR-C1',
    itemId: 'C1',
    stateCode: 'OR',
    title: 'Personnel Record Requirements',
    description: 'Oregon requires specific personnel record contents per OAR 333-536-0053',
    severity: 'high',
    regulationCode: '333-536-0053',
    regulationName: 'Personnel Records',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following do you maintain in EVERY caregiver\'s personnel file?',
        inputType: 'checklist',
        options: [
          { value: 'screening', label: 'Pre-employment screening (application, interview, references) - 333-536-0053(1)' },
          { value: 'background', label: 'Criminal records check and fitness determination - 333-536-0053(2)' },
          { value: 'qualifications', label: 'Position qualifications and licensure verification - 333-536-0053(3-4)' },
          { value: 'job_desc', label: 'Signed position description - 333-536-0053(5)' },
          { value: 'training', label: 'Orientation, training, competency evaluations - 333-536-0053(6)' },
          { value: 'service_level', label: 'Highest service level caregiver may provide - 333-536-0053(7)' },
          { value: 'performance', label: 'Annual performance evaluation - 333-536-0053(8)' },
          { value: 'drivers', label: 'Driver license and insurance (if transporting) - 333-536-0053(9)' },
        ],
        required: true,
        helpText: 'Per 333-536-0053: Personnel record must include all these elements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per 333-536-0053.',
      atRisk: 'Some personnel files may be missing required documents.',
      nonCompliant: 'Personnel files missing critical required documentation.',
      unknown: 'Audit personnel files against 333-536-0053 requirements.',
    },
    stateNotes: [
      'Pre-employment screening documented per 333-536-0053(1)',
      'Criminal records check and fitness determination per 333-536-0053(2)',
      'Job description must identify highest service level per 333-536-0053(5)',
      'Annual performance evaluation required per 333-536-0053(8)',
      'Driver license/insurance if transporting per 333-536-0053(9)',
    ],
  },

  {
    id: 'OR-C2',
    itemId: 'C2',
    stateCode: 'OR',
    title: 'Professional License Verification',
    description: 'Oregon requires verification of health-related licenses per OAR 333-536-0053(4)',
    severity: 'high',
    regulationCode: '333-536-0053(4)',
    regulationName: 'License',
    agencyName: 'Oregon State Board of Nursing',
    agencyAbbr: 'OSBN',
    agencyPhone: '(971) 673-0685',
    agencyUrl: 'https://www.oregon.gov/osbn',
    questions: [
      {
        id: 'q1',
        question: 'For staff with health-care licenses (RN, LPN, CNA), do you verify before hire that license is current and in good standing?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always verified before first assignment' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
          { value: 'na', label: 'N/A - no licensed staff' },
        ],
        required: true,
        helpText: 'Per 333-536-0053(4): Evidence that license is current and no board action preventing service.',
      },
      {
        id: 'q2',
        question: 'Do you require staff to report any licensing board actions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - required per policy' },
          { value: 'no', label: 'No requirement' },
        ],
        helpText: 'Per 333-536-0052(7)(b): Caregivers must report board actions that would prevent service.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification is complete.',
      atRisk: 'License verification may be inconsistent.',
      nonCompliant: 'Professional licenses not being verified.',
      unknown: 'Verify license verification procedures.',
    },
    stateNotes: [
      'Verify license is current and in good standing per 333-536-0053(4)',
      'Verify no board action preventing service per 333-536-0053(4)',
      'Staff must report new arrests/charges/convictions per 333-536-0052(7)(a)',
      'Staff must report board actions per 333-536-0052(7)(b)',
    ],
  },

  // ============================================
  // SECTION D: TRAINING REQUIREMENTS
  // ============================================
  {
    id: 'OR-D1',
    itemId: 'D1',
    stateCode: 'OR',
    title: 'Agency-Specific Orientation',
    description: 'Oregon requires 4-hour orientation before independent services per OAR 333-536-0070(5)',
    severity: 'high',
    regulationCode: '333-536-0070(5)',
    regulationName: 'Orientation',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL caregivers complete 4-hour agency orientation BEFORE independently providing services?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all complete 4 hours before independent service' },
          { value: 'most', label: 'Most do, some may start before completion' },
          { value: 'no', label: 'No formal orientation or less than 4 hours' },
        ],
        required: true,
        helpText: 'Per 333-536-0070(5): Minimum 4 hours orientation before independently providing services.',
      },
      {
        id: 'q2',
        question: 'Does your orientation include all required topics?',
        inputType: 'checklist',
        options: [
          { value: 'job_desc', label: 'Job description and requirements - 333-536-0070(5)(a)' },
          { value: 'rights', label: 'Client rights - 333-536-0070(5)(b)' },
          { value: 'ethics', label: 'Ethics and confidentiality - 333-536-0070(5)(c)' },
          { value: 'policies', label: 'Agency policies and procedures - 333-536-0070(5)(d)' },
          { value: 'services', label: 'Description of services provided - 333-536-0070(5)(e)' },
          { value: 'supervision', label: 'Assignment and supervision - 333-536-0070(5)(f)' },
          { value: 'documentation', label: 'Documentation requirements - 333-536-0070(5)(g)' },
          { value: 'med_reminding', label: 'Medication reminding limitations - 333-536-0070(5)(i)' },
        ],
        helpText: 'Per 333-536-0070(5): Orientation must include these topics.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency orientation meets 333-536-0070(5) requirements.',
      atRisk: 'Orientation may not be completed before independent services.',
      nonCompliant: 'Caregivers providing services without required 4-hour orientation.',
      unknown: 'Review orientation procedures.',
    },
    stateNotes: [
      'Minimum 4 hours orientation per 333-536-0070(5)',
      'May be online or in-person',
      'Must complete before independently providing services',
      'Must include job description, client rights, ethics, policies, documentation',
      'Must cover medication reminding limitations per 333-536-0070(5)(i)',
    ],
  },

  {
    id: 'OR-D2',
    itemId: 'D2',
    stateCode: 'OR',
    title: 'Initial Caregiver Training',
    description: 'Oregon requires 8 hours initial training per OAR 333-536-0070(7)',
    severity: 'high',
    regulationCode: '333-536-0070(7)',
    regulationName: 'Initial Training',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL caregivers complete 8 hours initial training with proper timing?',
        inputType: 'select',
        options: [
          { value: 'compliant', label: 'Yes - 2 hours before services, 6 hours within 120 days' },
          { value: 'partial', label: 'Training completed but timing may not be followed' },
          { value: 'no', label: 'No / not 8 hours' },
        ],
        required: true,
        helpText: 'Per 333-536-0070(7): 8 hours total - 2 before services, 6 within 120 days.',
      },
      {
        id: 'q2',
        question: 'Does your initial training include required topics?',
        inputType: 'checklist',
        options: [
          { value: 'duties', label: 'Caregiver duties and responsibilities - 333-536-0070(7)(d)(A)' },
          { value: 'emergency', label: 'Medical emergency response - 333-536-0070(7)(d)(B)' },
          { value: 'behaviors', label: 'Dealing with adverse behaviors - 333-536-0070(7)(d)(C)' },
          { value: 'nutrition', label: 'Nutrition and hydration - 333-536-0070(7)(d)(D)' },
          { value: 'personal_care', label: 'Safe personal care techniques - 333-536-0070(7)(d)(E)' },
          { value: 'infection', label: 'Hand washing and infection control - 333-536-0070(7)(d)(G)' },
          { value: 'body_mechanics', label: 'Body mechanics - 333-536-0070(7)(d)(H)' },
          { value: 'first_aid', label: 'Abdominal thrust and first aid - 333-536-0070(7)(d)(M)' },
        ],
        helpText: 'Per 333-536-0070(7)(d): Initial training must cover these topics.',
      },
      {
        id: 'q3',
        question: 'Do you document competency evaluation (observation + written/oral testing)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - both observation and testing documented' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 333-536-0070(6): Competency evaluation must include observation AND written/oral testing.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      // Training not covering required topics per 333-536-0070(7)(d)
      { condition: 'countAtLeast', questionId: 'q2', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Initial caregiver training meets 333-536-0070(7) requirements.',
      atRisk: 'Initial training timing, competency evaluation, or topic coverage may not meet requirements per 333-536-0070(7)(d).',
      nonCompliant: 'Caregivers not receiving required 8-hour initial training or training missing critical topics.',
      unknown: 'Review initial training procedures.',
    },
    stateNotes: [
      '8 hours total per 333-536-0070(7)',
      '2 hours BEFORE providing services per 333-536-0070(7)(a)',
      '6 hours within 120 days of hire per 333-536-0070(7)(b)',
      'Up to 1 hour competency evaluation counts toward 8 hours per 333-536-0070(7)(c)',
      'Competency = direct observation + written/oral test per 333-536-0070(6)',
      'Qualified trainer required per 333-536-0005(38)',
    ],
  },

  {
    id: 'OR-D3',
    itemId: 'D3',
    stateCode: 'OR',
    title: 'Medication Services Training',
    description: 'Oregon requires 4 hours medication training for staff providing medication services per OAR 333-536-0070(8)',
    severity: 'high',
    regulationCode: '333-536-0070(8)',
    regulationName: 'Medication Training',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide medication assistance or administration?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - medication reminding only or no medication services' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL caregivers providing medication services complete 4-hour medication training BEFORE providing such services?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all complete 4 hours before medication services' },
          { value: 'most', label: 'Most complete training' },
          { value: 'no', label: 'No / not tracking' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 333-536-0070(8): 4 hours medication training required before providing medication services.',
      },
      {
        id: 'q3',
        question: 'Do caregivers demonstrate competency via return demonstration before providing medication services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - qualified individual evaluates via return demonstration' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 333-536-0070(8)(c): Return demonstration required before providing medication services.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: No training when providing medication services
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: No competency demonstration when providing medication services
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // AT_RISK: Only most staff trained
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'most' },
      ], result: 'AT_RISK' },
      // COMPLIANT: Don't provide medication services (doesn't apply)
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      // COMPLIANT: All trained with competency demonstration
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medication training meets 333-536-0070(8) requirements or medication services not provided.',
      atRisk: 'Some staff providing medication services may lack proper training.',
      nonCompliant: 'Staff providing medication services without required training or competency demonstration.',
      unknown: 'Review medication training procedures.',
    },
    stateNotes: [
      '4 hours medication training required per 333-536-0070(8)',
      'Must complete BEFORE providing medication services',
      'Training by qualified individual or entity per 333-536-0070(8)(b)',
      'Return demonstration required per 333-536-0070(8)(c)',
      'Qualified individual must verify competency per 333-536-0070(8)(c)',
      'Applies to Basic, Intermediate, and Comprehensive agencies',
    ],
  },

  {
    id: 'OR-D4',
    itemId: 'D4',
    stateCode: 'OR',
    title: 'Annual Continuing Education',
    description: 'Oregon requires 6-7 hours annual education per OAR 333-536-0070(14)',
    severity: 'medium',
    regulationCode: '333-536-0070(14)',
    regulationName: 'Annual Education',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL caregivers complete required annual education?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - 6 hours (7 if medication services)' },
          { value: 'most', label: 'Most complete annual education' },
          { value: 'no', label: 'No annual education program' },
        ],
        required: true,
        helpText: 'Per 333-536-0070(14): 6 hours annual education (plus 1 hour for medication services).',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual education meets 333-536-0070(14) requirements.',
      atRisk: 'Some caregivers may not complete annual education.',
      nonCompliant: 'No annual education program in place.',
      unknown: 'Review annual education procedures.',
    },
    stateNotes: [
      '6 hours annual education per 333-536-0070(14)',
      'Plus 1 additional hour if providing medication services',
      'Training from qualified trainer, individual, or entity',
      'Related to caregiver duties',
    ],
  },

  {
    id: 'OR-D5',
    itemId: 'D5',
    stateCode: 'OR',
    title: 'Training Documentation',
    description: 'Oregon requires specific training documentation per OAR 333-536-0070(9)',
    severity: 'medium',
    regulationCode: '333-536-0070(9)',
    regulationName: 'Training Records',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you document all required training elements?',
        inputType: 'checklist',
        options: [
          { value: 'content', label: 'Content of training for each topic - 333-536-0070(9)(a)' },
          { value: 'dates', label: 'Dates, times, topics covered - 333-536-0070(9)(b)' },
          { value: 'trainer', label: 'Trainer name and signature - 333-536-0070(9)(c)' },
          { value: 'return_demo', label: 'Evidence of successful return demonstrations (medication) - 333-536-0070(9)(d)(A)' },
          { value: 'competency', label: 'Competency statement from qualified individual (medication) - 333-536-0070(9)(d)(B)' },
        ],
        required: true,
        helpText: 'Per 333-536-0070(9): Training documentation must include these elements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Training documentation meets 333-536-0070(9) requirements.',
      atRisk: 'Training documentation may be incomplete.',
      nonCompliant: 'Training documentation missing required elements.',
      unknown: 'Review training documentation.',
    },
    stateNotes: [
      'Maintain in personnel record per 333-536-0070(9)',
      'Include content, dates, times, topics per 333-536-0070(9)(a-b)',
      'Include trainer name and signature per 333-536-0070(9)(c)',
      'For medication: return demonstration evidence and competency statement per 333-536-0070(9)(d)',
    ],
  },

  // ============================================
  // SECTION E: CLIENT SERVICES
  // ============================================
  {
    id: 'OR-E1',
    itemId: 'E1',
    stateCode: 'OR',
    title: 'Disclosure Statement Requirements',
    description: 'Oregon requires written disclosure before services per OAR 333-536-0055',
    severity: 'high',
    regulationCode: '333-536-0055',
    regulationName: 'Disclosure',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written disclosure statements to ALL clients before services begin?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all clients sign before services' },
          { value: 'most', label: 'Most clients, some may be missing' },
          { value: 'no', label: 'No formal disclosure process' },
        ],
        required: true,
        helpText: 'Per 333-536-0055(2): Written disclosure signed by client or representative before services.',
      },
      {
        id: 'q2',
        question: 'Does your disclosure statement include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'classification', label: 'License classification and services offered - 333-536-0055(3)(a)' },
          { value: 'med_quals', label: 'Medication service qualifications (if applicable) - 333-536-0055(3)(b)' },
          { value: 'scope', label: 'Statement that agency cannot manage unstable/unpredictable conditions - 333-536-0055(3)(c)' },
          { value: 'charges', label: 'Charges for services - 333-536-0055(3)(e)' },
          { value: 'billing', label: 'Billing methods and payment - 333-536-0055(3)(g)' },
          { value: 'termination', label: 'Termination criteria and conditions - 333-536-0055(3)(j)' },
          { value: 'contact', label: 'How to contact administrator during service hours - 333-536-0055(3)(l)' },
          { value: 'rights', label: 'Copy of client rights per 333-536-0060 - 333-536-0055(3)(n)' },
        ],
        helpText: 'Per 333-536-0055(3): Disclosure must include all these elements.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Disclosure statements meet 333-536-0055 requirements.',
      atRisk: 'Some clients may not have received disclosure statements.',
      nonCompliant: 'Providing services without required disclosure statements.',
      unknown: 'Review disclosure statement procedures.',
    },
    stateNotes: [
      'Written disclosure required before services per 333-536-0055(2)',
      'Signed by client or representative',
      'Must be in client record per 333-536-0055(2)',
      'Must include client rights per 333-536-0055(3)(n)',
      'Must include statement about stable/predictable condition scope per 333-536-0055(3)(c)',
    ],
  },

  {
    id: 'OR-E2',
    itemId: 'E2',
    stateCode: 'OR',
    title: 'Service Plan Requirements',
    description: 'Oregon requires written service plans within 7 days per OAR 333-536-0065',
    severity: 'high',
    regulationCode: '333-536-0065',
    regulationName: 'Service Plan',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete written service plans within 7 days of service initiation?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always within 7 days' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No formal service plan process' },
        ],
        required: true,
        helpText: 'Per 333-536-0065(3): Service plan within 7 days after initiation of services.',
      },
      {
        id: 'q2',
        question: 'Does your service plan include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'client_info', label: 'Client name, address, phone, medical conditions - 333-536-0065(4)(a)' },
          { value: 'provider', label: 'Primary care provider name - 333-536-0065(4)(a)(D)' },
          { value: 'services', label: 'Services to be provided, specifying tasks - 333-536-0065(4)(c)' },
          { value: 'schedule', label: 'Schedule specifying range of hours per month - 333-536-0065(4)(b)' },
          { value: 'needs', label: 'Pertinent client needs for safe care - 333-536-0065(4)(d)' },
          { value: 'signatures', label: 'Signed and dated by all participants - 333-536-0065(3)(c)' },
        ],
        helpText: 'Per 333-536-0065(4): Service plan must include these elements.',
      },
      {
        id: 'q3',
        question: 'Do you review service plans with caregivers before first client assignment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - reviewed and confirmed before first care' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 333-536-0065(5): Must review with each caregiver before initial delivery of care.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      // Service plan missing required elements per 333-536-0065(4)
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service plans meet 333-536-0065 requirements.',
      atRisk: 'Service plans may be delayed, not reviewed with caregivers, or missing required elements per 333-536-0065(4).',
      nonCompliant: 'Services provided without required service plans or plans missing critical elements.',
      unknown: 'Review service plan procedures.',
    },
    stateNotes: [
      'Written service plan within 7 days per 333-536-0065(3)',
      'Client-directed per 333-536-0065(4)',
      'Signed and dated by all participants per 333-536-0065(3)(c)',
      'Review with each caregiver before first assignment per 333-536-0065(5)',
      'Document caregiver confirmation per 333-536-0065(5)(b)',
      'Maintain original and updates in client record per 333-536-0065(7)',
    ],
  },

  {
    id: 'OR-E3',
    itemId: 'E3',
    stateCode: 'OR',
    title: 'Initial Visit and Monitoring',
    description: 'Oregon requires initial visit 7-30 days and quarterly monitoring per OAR 333-536-0066',
    severity: 'high',
    regulationCode: '333-536-0066',
    regulationName: 'Monitoring',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct initial visits at client residence between days 7-30 after start of care?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always between days 7-30' },
          { value: 'usually', label: 'Usually, but timing varies' },
          { value: 'no', label: 'No initial visit process' },
        ],
        required: true,
        helpText: 'Per 333-536-0066(1)(a): Initial visit within 30 days, between 7th and 30th day.',
      },
      {
        id: 'q2',
        question: 'Do you conduct quarterly monitoring visits after the initial visit?',
        inputType: 'select',
        options: [
          { value: 'quarterly', label: 'Yes - quarterly (in-person no more than 6 months apart)' },
          { value: 'sometimes', label: 'Sometimes, not consistently quarterly' },
          { value: 'no', label: 'No monitoring visit program' },
        ],
        helpText: 'Per 333-536-0066(1)(b): Quarterly monitoring after initial visit. In-person max 6 months apart per 333-536-0066(2).',
      },
      {
        id: 'q3',
        question: 'Do monitoring visits document all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'techniques', label: 'Appropriate and safe techniques used - 333-536-0066(4)(a)' },
          { value: 'plan_followed', label: 'Service plan followed as written - 333-536-0066(4)(b)' },
          { value: 'plan_meeting_needs', label: 'Service plan meeting client needs - 333-536-0066(4)(c)' },
          { value: 'training', label: 'Caregiver received sufficient training - 333-536-0066(4)(d)' },
          { value: 'satisfaction', label: 'Client satisfied with caregiver - 333-536-0066(4)(e)' },
          { value: 'adverse_events', label: 'Any adverse events/complaints since last visit - 333-536-0066(4)(g)' },
        ],
        helpText: 'Per 333-536-0066(4): Monitoring must document these elements.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // Monitoring visits missing required documentation per 333-536-0066(4)
      { condition: 'countAtLeast', questionId: 'q3', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q3', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Initial visit and monitoring meet 333-536-0066 requirements.',
      atRisk: 'Initial visit timing, monitoring may be inconsistent, or documentation may be incomplete per 333-536-0066(4).',
      nonCompliant: 'Required initial visits or monitoring not being conducted or missing critical documentation.',
      unknown: 'Review monitoring procedures.',
    },
    stateNotes: [
      'Initial visit between days 7-30 per 333-536-0066(1)(a)',
      'Quarterly monitoring after initial visit per 333-536-0066(1)(b)',
      'In-person visits max 6 months apart per 333-536-0066(2)',
      'Quarterly visits may be phone/electronic if conditions met per 333-536-0066(1)(b)',
      'Signed by administrator/designee/delegate AND client per 333-536-0066(3)',
    ],
  },

  {
    id: 'OR-E4',
    itemId: 'E4',
    stateCode: 'OR',
    title: 'Client Rights Documentation',
    description: 'Oregon requires client rights notification per OAR 333-536-0060',
    severity: 'high',
    regulationCode: '333-536-0060',
    regulationName: 'Client Rights',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written notice of client rights to ALL clients before furnishing care?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all clients before care begins' },
          { value: 'most', label: 'Most clients' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'Per 333-536-0060(2): Written notice of rights before furnishing care.',
      },
      {
        id: 'q2',
        question: 'Does your client rights notice include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'dignity', label: 'Right to dignity and respect - 333-536-0060(1)(a)' },
          { value: 'abuse_free', label: 'Right to be free from abuse/neglect - 333-536-0060(1)(d)' },
          { value: 'grievance', label: 'Right to voice grievances without reprisal - 333-536-0060(1)(g)' },
          { value: 'participate', label: 'Right to participate in care planning - 333-536-0060(1)(i)' },
          { value: 'records', label: 'Right to access records - 333-536-0060(1)(j)' },
          { value: 'confidentiality', label: 'Right to confidentiality - 333-536-0060(1)(k)' },
          { value: 'termination', label: 'Right to 30-day termination notice - 333-536-0060(1)(m)' },
          { value: 'oha_contact', label: 'OHA contact info for complaints - 333-536-0060(2)(b)' },
        ],
        helpText: 'Per 333-536-0060: Client rights must include these elements.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client rights notification meets 333-536-0060 requirements.',
      atRisk: 'Some clients may not have received rights notification.',
      nonCompliant: 'Client rights not being provided as required.',
      unknown: 'Review client rights procedures.',
    },
    stateNotes: [
      'Written notice before furnishing care per 333-536-0060(2)',
      'Part of disclosure statement per 333-536-0055(3)(n)',
      'Must include OHA contact info for complaints per 333-536-0060(2)(b)',
      'Right to 30-day termination notice with exceptions per 333-536-0060(1)(m)',
      'Agency authority to examine records noted per 333-536-0060(2)(c)',
    ],
  },

  {
    id: 'OR-E5',
    itemId: 'E5',
    stateCode: 'OR',
    title: 'Medication Self-Direction Evaluation',
    description: 'Oregon requires 90-day re-evaluation of client self-direction for medication services per OAR 333-536-0045(5)',
    severity: 'high',
    regulationCode: '333-536-0045(5)',
    regulationName: 'Self-Direction',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide medication assistance or reminding services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No medication services' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you evaluate whether clients can continue to self-direct at least every 90 days?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - every 90 days for all medication clients' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No re-evaluation process' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 333-536-0045(5): Evaluate self-direction ability minimum every 90 days.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medication self-direction evaluation meets requirements.',
      atRisk: 'Self-direction evaluations may not be conducted every 90 days.',
      nonCompliant: 'Required 90-day self-direction evaluations not being conducted.',
      unknown: 'Review medication client evaluation procedures.',
    },
    stateNotes: [
      'Evaluate self-direction minimum every 90 days per 333-536-0045(5)',
      'Self-direct = knows reason, dose, route, time for each med per 333-536-0005(41)',
      'If cannot self-direct: transfer to higher classification agency within 30 days per 333-536-0045(5)',
      'Document evaluation in client record per 333-536-0045(6)',
    ],
  },

  // ============================================
  // SECTION F: CLIENT RECORDS
  // ============================================
  {
    id: 'OR-F1',
    itemId: 'F1',
    stateCode: 'OR',
    title: 'Client Record Requirements',
    description: 'Oregon requires comprehensive client records per OAR 333-536-0085',
    severity: 'high',
    regulationCode: '333-536-0085',
    regulationName: 'Client Records',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do your client records include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'id', label: 'Identification data - 333-536-0085(2)(a)' },
          { value: 'start_date', label: 'Start-of-service date - 333-536-0085(2)(c)' },
          { value: 'disclosure', label: 'Disclosure documents - 333-536-0085(2)(d)' },
          { value: 'rights', label: 'Client rights documentation - 333-536-0085(2)(e)' },
          { value: 'service_plan', label: 'Service plan and updates - 333-536-0085(2)(g)' },
          { value: 'daily_notes', label: 'Daily caregiver notes - 333-536-0085(2)(h)' },
          { value: 'agreement', label: 'Service and financial agreement - 333-536-0085(2)(i)' },
          { value: 'end_summary', label: 'End-of-service summary - 333-536-0085(2)(k)' },
        ],
        required: true,
        helpText: 'Per 333-536-0085(2): Client records must include all these elements.',
      },
      {
        id: 'q2',
        question: 'Are all client record entries dated and signed/authenticated?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not consistently' },
        ],
        helpText: 'Per 333-536-0085(4): All entries dated and signed or authenticated.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client records meet 333-536-0085 requirements.',
      atRisk: 'Client records may be missing required elements.',
      nonCompliant: 'Client records missing critical required elements.',
      unknown: 'Audit client records against 333-536-0085.',
    },
    stateNotes: [
      'Maintain for every client (except housekeeping only) per 333-536-0085(1)',
      'Daily caregiver notes: tasks completed, observations, concerns per 333-536-0085(2)(h)',
      'All entries dated and signed per 333-536-0085(4)',
      'Electronic authentication acceptable per 333-536-0085(4)',
      'Protect from unauthorized access, fire, water, theft per 333-536-0085(6)',
    ],
  },

  {
    id: 'OR-F2',
    itemId: 'F2',
    stateCode: 'OR',
    title: 'Record Retention',
    description: 'Oregon requires 7-year record retention per OAR 333-536-0085(9)',
    severity: 'high',
    regulationCode: '333-536-0085(9)',
    regulationName: 'Record Retention',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client records after last end-of-service?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer (meets requirement)' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per 333-536-0085(9): Retain at least 7 years after last end-of-service.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets 7-year requirement per 333-536-0085(9).',
      atRisk: 'Oregon requires 7 years retention. Update your policy.',
      nonCompliant: 'Record retention does not meet 7-year requirement.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      '7 years after last end-of-service per 333-536-0085(9)',
      'Records are agency property per 333-536-0085(10)',
      'Change of ownership: records remain with agency per 333-536-0085(11)',
      'If terminating business: notify OHA of storage location per 333-536-0085(12)',
    ],
  },

  // ============================================
  // SECTION G: POLICIES AND QUALITY
  // ============================================
  {
    id: 'OR-G1',
    itemId: 'G1',
    stateCode: 'OR',
    title: 'Required Policies and Procedures',
    description: 'Oregon requires written policies per OAR 333-536-0051',
    severity: 'high',
    regulationCode: '333-536-0051',
    regulationName: 'Policies',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written policies and procedures for all required areas?',
        inputType: 'checklist',
        options: [
          { value: 'admin', label: 'Administrative operations - 333-536-0051(1)' },
          { value: 'personnel', label: 'Personnel operations including travel, reporting, care practices - 333-536-0051(2)' },
          { value: 'preemployment', label: 'Preemployment screening and background checks - 333-536-0051(3)' },
          { value: 'notification', label: 'Client notification (costs, deposits, cancellation, termination) - 333-536-0051(4)' },
          { value: 'records', label: 'Client records management - 333-536-0051(5)' },
          { value: 'infection', label: 'Infection control - 333-536-0051(6)' },
          { value: 'emergency', label: 'Medical and non-medical emergency response - 333-536-0051(7)' },
          { value: 'medication', label: 'Medication reminding, assistance, administration - 333-536-0051(8)' },
          { value: 'nursing', label: 'Nursing services (if comprehensive) - 333-536-0051(9)' },
          { value: 'qa', label: 'Quality assessment and performance improvement - 333-536-0051(10)' },
        ],
        required: true,
        helpText: 'Per 333-536-0051: Must develop, implement, and enforce written policies for all areas.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Written policies meet 333-536-0051 requirements.',
      atRisk: 'Some required policies may be missing.',
      nonCompliant: 'Multiple required policies are missing.',
      unknown: 'Review policies against 333-536-0051 requirements.',
    },
    stateNotes: [
      'Must develop, implement, AND enforce per 333-536-0051',
      'Investigation policy for complaints, adverse events, abuse allegations per 333-536-0051(2)(d)',
      'Personnel must include travel requirements and reporting per 333-536-0051(2)(a-b)',
      'QAPI activities required per 333-536-0051(10)',
    ],
  },

  {
    id: 'OR-G2',
    itemId: 'G2',
    stateCode: 'OR',
    title: 'Quality Assessment and Performance Improvement',
    description: 'Oregon requires QAPI program per OAR 333-536-0090',
    severity: 'medium',
    regulationCode: '333-536-0090',
    regulationName: 'QAPI',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a QAPI program that includes required elements?',
        inputType: 'checklist',
        options: [
          { value: 'identify', label: 'Method to identify, analyze, correct adverse events - 333-536-0090(1)' },
          { value: 'track', label: 'Track quality indicators (high risk, high volume, problem prone) - 333-536-0090(2)' },
          { value: 'preventive', label: 'Develop and implement preventive strategies - 333-536-0090(3)' },
          { value: 'committee', label: 'QI committee (admin, caregiver, RN if applicable) - 333-536-0090(4)' },
          { value: 'quarterly', label: 'Activities conducted and documented quarterly - 333-536-0090(5)' },
        ],
        required: true,
        helpText: 'Per 333-536-0090: Agency-wide QAPI program required.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets 333-536-0090 requirements.',
      atRisk: 'QAPI program may be incomplete.',
      nonCompliant: 'Required QAPI program elements missing.',
      unknown: 'Review QAPI requirements.',
    },
    stateNotes: [
      'Agency-wide program required per 333-536-0090',
      'Committee must include admin, caregiver, RN (if intermediate/comprehensive) per 333-536-0090(4)',
      'Quarterly activities with documentation per 333-536-0090(5)',
      'Documentation includes date, members, titles, minutes per 333-536-0090(5)',
    ],
  },

  {
    id: 'OR-G3',
    itemId: 'G3',
    stateCode: 'OR',
    title: 'Infection Control Program',
    description: 'Oregon requires infection control program per OAR 333-536-0082',
    severity: 'high',
    regulationCode: '333-536-0082',
    regulationName: 'Infection Control',
    agencyName: 'Oregon Health Authority',
    agencyAbbr: 'OHA',
    agencyPhone: '(971) 673-0540',
    agencyUrl: 'https://www.oregon.gov/oha/PH/PROVIDERPARTNERRESOURCES/HEALTHCAREPROVIDERSFACILITIES/HEALTHCAREHEALTHCAREREGULATIONQUALITYIMPROVEMENT',
    questions: [
      {
        id: 'q1',
        question: 'Does your infection control program include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'surveillance', label: 'Active surveillance, identification, prevention, control - 333-536-0082(1)' },
          { value: 'sharps', label: 'Sharps disposal per OAR 333-056-0050 - 333-536-0082(2)' },
          { value: 'standard', label: 'Standard precautions (hand hygiene, respiratory, PPE) - 333-536-0082(3)(a)' },
          { value: 'ppe', label: 'PPE and equipment availability - 333-536-0082(3)(b)' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure procedures - 333-536-0082(3)(c)' },
          { value: 'training', label: 'Education and training for staff, clients, caregivers - 333-536-0082(4)' },
        ],
        required: true,
        helpText: 'Per 333-536-0082: Infection control program required.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets 333-536-0082 requirements.',
      atRisk: 'Infection control program may be incomplete.',
      nonCompliant: 'Required infection control elements missing.',
      unknown: 'Review infection control requirements.',
    },
    stateNotes: [
      'Prevent and control infections and communicable diseases per 333-536-0082',
      'Sharps disposal per OAR 333-056-0050 per 333-536-0082(2)',
      'Standard precautions including hand hygiene, respiratory hygiene per 333-536-0082(3)(a)',
      'Training for staff, clients, caregivers per 333-536-0082(4)',
    ],
  },

  {
    id: 'OR-G4',
    itemId: 'G4',
    stateCode: 'OR',
    title: 'Mandatory Abuse Reporting',
    description: 'Oregon requires abuse reporting per ORS 124.050-124.095 and ORS 430.735',
    severity: 'high',
    regulationCode: '124.050-124.095, ORS 430.735',
    regulationName: 'Mandatory Abuse Reporting',
    agencyName: 'ODHS Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(855) 503-7233',
    agencyUrl: 'https://www.oregon.gov/odhs/safety/Pages/abuse.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL staff trained on mandatory abuse reporting requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all staff trained on recognition and reporting' },
          { value: 'most', label: 'Most staff trained' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'In-home care staff are mandatory reporters under ORS 124.050 and ORS 430.765.',
      },
      {
        id: 'q2',
        question: 'Do staff know the APS hotline (855-503-7233) for reporting?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure' },
        ],
        helpText: 'Per 333-536-0052(6)(i): Administrator must ensure timely reporting to appropriate authorities.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse reporting training complete for all staff.',
      atRisk: 'Some staff may lack abuse reporting training.',
      nonCompliant: 'Staff not trained on mandatory abuse reporting.',
      unknown: 'Verify abuse reporting training.',
    },
    stateNotes: [
      'In-home care staff are mandatory reporters',
      'ORS 124.050-124.095: Elderly persons and adults with disabilities',
      'ORS 430.735-430.765: Adults with mental illness or developmental disabilities',
      'Report to ODHS, OHA, or law enforcement per 333-536-0052(6)(i)',
      'APS hotline: (855) 503-7233',
    ],
  },
];

export default OREGON_QUESTIONS;